import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import LogHoursModal from './LogHoursModal';
import './Salon.css';

const formatMonthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const formatDateStr = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const getDaysInMonth = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const lastDay = new Date(year, month + 1, 0).getDate();
  const days = [];
  for (let d = 1; d <= lastDay; d++) {
    days.push(new Date(year, month, d));
  }
  return days;
};

// Expands a start/end date range (inclusive) into a set of 'YYYY-MM-DD' strings
const expandDateRange = (startDate, endDate) => {
  const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
  const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
  const cursor = new Date(startYear, startMonth - 1, startDay);
  const end = new Date(endYear, endMonth - 1, endDay);
  const dates = [];
  while (cursor <= end) {
    dates.push(formatDateStr(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

// New entries can only be logged/deleted for the current or previous calendar month (editing existing entries is unrestricted)
const isLoggableMonth = (monthDate) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  let prevYear = currentYear;
  let prevMonth = currentMonth - 1;
  if (prevMonth < 0) {
    prevMonth = 11;
    prevYear -= 1;
  }
  const isCurrent = monthDate.getFullYear() === currentYear && monthDate.getMonth() === currentMonth;
  const isPrevious = monthDate.getFullYear() === prevYear && monthDate.getMonth() === prevMonth;
  return isCurrent || isPrevious;
};

const TimeSheetTab = ({ salonId, userRole }) => {
  const { t, getDayName, getMonthName } = useLanguage();
  const isAdmin = userRole === 'owner' || userRole === 'admin';

  const [monthDate, setMonthDate] = useState(() => new Date());
  const [members, setMembers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [timeLogs, setTimeLogs] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [ptoDates, setPtoDates] = useState(() => new Set());
  const [holidayMap, setHolidayMap] = useState(() => new Map());
  const [weeklyHours, setWeeklyHours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [initialDate, setInitialDate] = useState(null);

  // Load the list of employees this admin/owner is allowed to view timesheets for.
  // Owners see everyone; admins see fellow admins and members, but not the owner's.
  useEffect(() => {
    if (!isAdmin) return;
    const fetchMembers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/salons/${salonId}/members`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const visible = userRole === 'owner'
          ? response.data.members
          : response.data.members.filter((m) => m.role !== 'owner');
        setMembers(visible);
        const self = visible.find((m) => m.isCurrentUser);
        if (self) setSelectedUserId(self.id);
      } catch (err) {
        console.error('Error fetching salon members:', err);
      }
    };
    fetchMembers();
  }, [salonId, isAdmin, userRole]);

  const isViewingSelf = !isAdmin || selectedUserId === null || !!members.find((m) => m.id === selectedUserId)?.isCurrentUser;

  const fetchTimeLogs = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const month = formatMonthKey(monthDate);

      const response = isViewingSelf
        ? await axios.get(`/api/salons/${salonId}/time-logs`, { ...config, params: { month } })
        : await axios.get(`/api/salons/${salonId}/time-logs/all`, { ...config, params: { month, userId: selectedUserId } });

      setTimeLogs(response.data.timeLogs);
      setTotalHours(response.data.totalHours);
      setError('');
    } catch (err) {
      console.error('Error fetching time logs:', err);
      setError(t('timeOff.hours.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [salonId, monthDate, isViewingSelf, selectedUserId, t]);

  useEffect(() => {
    fetchTimeLogs();
  }, [fetchTimeLogs]);

  // Approved PTO days are marked on the calendar and can't be newly logged against, same as weekends -
  // fetched once per viewed employee (not month-scoped) since the request list is small.
  useEffect(() => {
    const fetchApprovedPto = async () => {
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = isViewingSelf
          ? await axios.get(`/api/salons/${salonId}/pto/requests`, { ...config, params: { status: 'approved' } })
          : await axios.get(`/api/salons/${salonId}/pto/requests/all`, { ...config, params: { status: 'approved', userId: selectedUserId } });

        const requests = response.data.ptoRequests || [];
        const dates = new Set();
        requests.forEach((r) => {
          expandDateRange(r.start_date, r.end_date).forEach((d) => dates.add(d));
        });
        setPtoDates(dates);
      } catch (err) {
        console.error('Error fetching approved PTO days:', err);
      }
    };
    fetchApprovedPto();
  }, [salonId, isViewingSelf, selectedUserId]);

  // National holidays are salon-wide (not per-employee) - refetched only when the browsed year changes
  const year = monthDate.getFullYear();
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/salons/${salonId}/holidays`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { year }
        });
        const map = new Map();
        (response.data.holidays || []).forEach((h) => map.set(h.holiday_date, h.name));
        setHolidayMap(map);
      } catch (err) {
        console.error('Error fetching holidays:', err);
      }
    };
    fetchHolidays();
  }, [salonId, year]);

  // Expected weekly work hours for the viewed employee, set by an admin in PTO Settings
  useEffect(() => {
    const fetchWeeklyHours = async () => {
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(`/api/salons/${salonId}/weekly-hours`, { ...config, params: isViewingSelf ? {} : { userId: selectedUserId } });
        setWeeklyHours(response.data.weeklyHours);
      } catch (err) {
        console.error('Error fetching weekly hours:', err);
      }
    };
    fetchWeeklyHours();
  }, [salonId, isViewingSelf, selectedUserId]);

  const logsByDate = useMemo(() => {
    const map = {};
    timeLogs.forEach((log) => { map[log.work_date] = log; });
    return map;
  }, [timeLogs]);

  const days = useMemo(() => getDaysInMonth(monthDate), [monthDate]);
  const todayStr = formatDateStr(new Date());
  const canLogInMonth = isLoggableMonth(monthDate);

  // Expected hours = weekdays in the month that aren't approved PTO or a national holiday, at (weekly hours / 5) per day
  const expectedHours = useMemo(() => {
    if (weeklyHours === null) return null;
    const expectedDays = days.filter((day) => {
      const dow = day.getDay();
      if (dow === 0 || dow === 6) return false;
      const dateStr = formatDateStr(day);
      return !ptoDates.has(dateStr) && !holidayMap.has(dateStr);
    }).length;
    return Math.round(expectedDays * (weeklyHours / 5) * 100) / 100;
  }, [days, ptoDates, holidayMap, weeklyHours]);

  const handlePrevMonth = () => {
    setMonthDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setMonthDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleDayRowClick = (dateStr, log) => {
    if (!isViewingSelf) return;
    if (log) {
      setEditingLog(log);
      setInitialDate(null);
    } else {
      if (!canLogInMonth || ptoDates.has(dateStr)) return;
      setEditingLog(null);
      setInitialDate(dateStr);
    }
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingLog(null);
    setInitialDate(null);
  };

  const handleSaved = () => {
    fetchTimeLogs();
    handleModalClose();
  };

  const handleDelete = (e, logId) => {
    e.stopPropagation();
    if (!window.confirm(t('messages.confirm.deleteTimeLog'))) {
      return;
    }
    (async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/salons/${salonId}/time-logs/${logId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchTimeLogs();
      } catch (err) {
        console.error('Error deleting time log:', err);
        alert(err.response?.data?.error || t('timeOff.hours.failedToDelete'));
      }
    })();
  };

  return (
    <div>
      <div className="time-off-month-nav">
        <div className="time-off-month-controls">
          <button className="pagination-btn" onClick={handlePrevMonth}>‹</button>
          <span className="time-off-month-label">{getMonthName(monthDate.getMonth())} {monthDate.getFullYear()}</span>
          <button className="pagination-btn" onClick={handleNextMonth}>›</button>
        </div>
        {isAdmin && members.length > 0 && (
          <select
            className="time-off-select"
            value={selectedUserId || ''}
            onChange={(e) => setSelectedUserId(Number(e.target.value))}
          >
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.isCurrentUser ? t('timeOff.hours.me') : `${m.first_name} ${m.last_name}`}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="table-header time-off-summary-header">
        <div className="time-off-summary">
          {t('timeOff.hours.totalThisMonth')}: <strong>{totalHours}h</strong>
        </div>
        <div className="time-off-header-right">
          <div className="time-off-summary">
            {t('timeOff.hours.expectedThisMonth')}: <strong>{expectedHours === null ? t('timeOff.pto.notSet') : `${expectedHours}h`}</strong>
          </div>
          {isViewingSelf && !canLogInMonth && (
            <div className="time-off-readonly-note">{t('timeOff.hours.pastMonthReadOnly')}</div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">{t('common.loading')}...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div className="table-container">
          <div className="table-wrapper time-off-days-wrapper">
            <table className="data-table time-off-days-table">
              <thead>
                <tr>
                  <th>{t('timeOff.hours.date')}</th>
                  <th>{t('timeOff.hours.startTime')}</th>
                  <th>{t('timeOff.hours.endTime')}</th>
                  <th>{t('timeOff.hours.hoursWorked')}</th>
                  <th>{t('timeOff.hours.note')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {days.map((day) => {
                  const dateStr = formatDateStr(day);
                  const log = logsByDate[dateStr];
                  const dayOfWeek = day.getDay();
                  const isSunday = dayOfWeek === 0;
                  const isSaturday = dayOfWeek === 6;
                  const isToday = dateStr === todayStr;
                  // Sunday always shows/behaves as a regular non-working day, never as PTO or a holiday - Saturday can still be marked either
                  const isPtoDay = ptoDates.has(dateStr) && !isSunday;
                  const holidayName = !isSunday ? holidayMap.get(dateStr) : undefined;
                  const isHolidayDay = !!holidayName;
                  const canLogOnDay = canLogInMonth && !isPtoDay;

                  const isRowClickable = isViewingSelf && (log || canLogOnDay);

                  const rowClassNames = [
                    'time-off-day-row',
                    isSunday ? 'non-working' : '',
                    isSaturday ? 'saturday' : '',
                    isPtoDay ? 'pto-day' : '',
                    isHolidayDay ? 'holiday-day' : '',
                    isToday ? 'today' : '',
                    log ? 'logged' : '',
                    isRowClickable ? '' : 'read-only'
                  ].filter(Boolean).join(' ');

                  return (
                    <tr
                      key={dateStr}
                      className={rowClassNames}
                      onClick={isRowClickable ? () => handleDayRowClick(dateStr, log) : undefined}
                    >
                      <td>
                        <div className="time-off-day-cell">
                          <span className="time-off-day-weekday">{getDayName(dayOfWeek, true)}</span>
                          <span className="time-off-day-date">{day.getDate()}</span>
                          {isHolidayDay ? (
                            <span className="time-off-day-badge holiday" title={holidayName}>{holidayName}</span>
                          ) : isPtoDay ? (
                            <span className="time-off-day-badge pto">{t('timeOff.hours.ptoDay')}</span>
                          ) : isSunday ? (
                            <span className="time-off-day-badge">{t('timeOff.hours.nonWorkingDay')}</span>
                          ) : isSaturday ? (
                            <span className="time-off-day-badge saturday">{t('timeOff.hours.saturday')}</span>
                          ) : null}
                        </div>
                      </td>
                      <td>{log ? log.start_time : '—'}</td>
                      <td>{log ? log.end_time : '—'}</td>
                      <td>{log ? `${log.hours}h` : '—'}</td>
                      <td>{log?.note || '—'}</td>
                      <td>
                        {isViewingSelf && (
                          log ? (
                            canLogInMonth && (
                              <button className="action-button delete" onClick={(e) => handleDelete(e, log.id)} title={t('common.delete')}>🗑️</button>
                            )
                          ) : (
                            canLogOnDay && <span className="time-off-day-add">+ {t('timeOff.hours.logHours')}</span>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isViewingSelf && (
        <LogHoursModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          salonId={salonId}
          existingLog={editingLog}
          initialDate={initialDate}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default TimeSheetTab;
